import PageData from "../../PageData";
import {EventBus} from "../../events/EventBus";
import AdFormService from "../../services/AdFormService";
import Modal from "../../components/modals/Modal";
import ContactForm from "../../forms/ContactForm";
import ContactModal from "../../components/modals/ContactModal";
import {
    StickyContactButtonClickedEvent,
    StickyContactButtonClickedEventPayload,
    WhitelabelEvent
} from "../../events/Events";
import UserContactDataStorageService, {UserContactDataType} from "../../services/UserContactDataStorageService";
import {Section} from "../../services/Section";
import {Operations} from "../../forms/Operations";

export default class ProperatiLamudiProjectContactModal {

    private readonly pageData: PageData;
    private readonly eventBus: EventBus;
    private readonly adFormService: AdFormService;
    private readonly projectContactModalId: string = 'project-contact-modal';
    private readonly projectContactModalFormId: string = 'project-contact-modal-form';
    private readonly closeContactModalButtonId: string = 'close-contact-modal';
    private readonly desktopProjectContactModalFormSelector: string = '.js-project-page-form-container #adform-contact-form';
    private modal: Modal | undefined;
    private mobileContactForm: ContactForm | undefined;
    private desktopContactForm: ContactForm | undefined;
    private isSubmitted: boolean = false;

    constructor(pageData: PageData) {
        this.pageData = pageData;
        this.adFormService = new AdFormService();
        this.eventBus = EventBus.getInstance();
        this.initMobileForm(pageData);
        this.initDesktopForm(pageData);
    }

    private initMobileForm(pageData: PageData) {
        if (!document.getElementById(this.projectContactModalId)) return;
        this.modal = new Modal({
            pageData: pageData,
            element: document.getElementById(this.projectContactModalId)!,
            openTrigger: [], // we handle it via events
            closeTrigger: [], // we handle it via events
        });
        const mobileFormContainer = document.getElementById(this.projectContactModalFormId);
        if (mobileFormContainer) {
            const emailInput: HTMLInputElement = document.querySelector<HTMLInputElement>(`#${this.projectContactModalFormId} #form-email-contact`)!;
            const nameInput: HTMLInputElement = document.querySelector<HTMLInputElement>(`#${this.projectContactModalFormId} #form-name-contact`)!;
            const phoneInput: HTMLInputElement = document.querySelector<HTMLInputElement>(`#${this.projectContactModalFormId} #form-phone-contact`)!;
            this.mobileContactForm = new ContactForm({
                pageData,
                formContainer: mobileFormContainer,
                nameElement: nameInput,
                emailElement: emailInput,
                phoneElement: phoneInput,
                messageElement: document.querySelector<HTMLTextAreaElement>(`#${this.projectContactModalFormId} #form-message-contact`)!,
                submitElement: document.querySelector<HTMLElement>(`#${this.projectContactModalFormId} #request-info-send-button-contact`)!,
                onSubmit: () => this.onSubmit(),
                operations: new Operations(mobileFormContainer),
            });
            new ContactModal({
                modal: this.modal,
                contactForm: this.mobileContactForm,
                pageData,
            });
            this.fillFormInputs(emailInput, nameInput, phoneInput);
        }
        this.subscribeToEvents();
    }

    private initDesktopForm(pageData: PageData) {
        const desktopFormContainer = document.querySelector<HTMLElement>(this.desktopProjectContactModalFormSelector);
        if (desktopFormContainer) {
            this.adFormService.sendApplyImpression({
                pageViewId: this.pageData.pageViewId,
                propertyAdId: this.pageData.id,
                section: Section.SECTION_ADFORM_APPLY,
                step: 1,
                isProject: true,
            });
            const messageElement: HTMLTextAreaElement = document.createElement('textarea');
            messageElement.value = '';
            const emailInput: HTMLInputElement = document.querySelector<HTMLInputElement>(`${this.desktopProjectContactModalFormSelector} #form-email`)!;
            const nameInput: HTMLInputElement = document.querySelector<HTMLInputElement>(`${this.desktopProjectContactModalFormSelector} #form-name`)!;
            const phoneInput: HTMLInputElement = document.querySelector<HTMLInputElement>(`${this.desktopProjectContactModalFormSelector} #form-phone`)!;
            this.desktopContactForm = new ContactForm({
                pageData,
                formContainer: desktopFormContainer,
                nameElement: nameInput,
                emailElement: emailInput,
                phoneElement: phoneInput,
                messageElement: messageElement,
                submitElement: document.querySelector<HTMLElement>(`${this.desktopProjectContactModalFormSelector} #request-info-send-button`)!,
                onSubmit: () => this.onSubmitDesktopContactForm(),
                operations: new Operations(desktopFormContainer),
            });
            this.fillFormInputs(emailInput, nameInput, phoneInput);
        }
    }

    private fillFormInputs(emailInput: HTMLInputElement, nameInput: HTMLInputElement, phoneInput: HTMLInputElement) {
        new UserContactDataStorageService().fillInputsWithStoredValues(
            {input: emailInput, type: UserContactDataType.EMAIL},
            {input: nameInput, type: UserContactDataType.NAME},
            {input: phoneInput, type: UserContactDataType.PHONE},
        );
    }

    static init(pageData: PageData) {
        return new ProperatiLamudiProjectContactModal(pageData);
    }

    private onOpenedModal(payload: StickyContactButtonClickedEventPayload) {
        if (!this.isSubmitted) {
            this.adFormService.sendApplyImpression({
                pageViewId: payload.pageViewId,
                propertyAdId: payload.propertyAdId,
                section: payload.section,
                step: payload.step,
                isProject: true,
            });
        }
    }

    private subscribeToEvents() {
        this.eventBus.subscribeWithId(StickyContactButtonClickedEvent.TYPE, {
            id: 'project-sticky-contact-button-subscriber',
            callback: (event: WhitelabelEvent) => {
                const payload: StickyContactButtonClickedEventPayload = (event as StickyContactButtonClickedEvent).payload;
                this.modal!.onOpen = () => this.onOpenedModal(payload);
                this.modal!.open(this.projectContactModalId)();
            }
        });
        document.querySelector(`#${this.projectContactModalId} #${this.closeContactModalButtonId}`)!
            .addEventListener('click', this.modal!.close());
    }

    private onSubmit() {
        this.applyProject(
            {
                userName: this.mobileContactForm!.nameValue(),
                userEmail: this.mobileContactForm!.emailValue(),
                userPhone: this.mobileContactForm!.phoneValue(),
                message: this.mobileContactForm!.messageValue(),
                section: this.mobileContactForm!.getSection() || Section.SECTION_ADPAGE_STICKY_BOTTOM_CONTACT_FORM,
            }
        )
    }

    private onSubmitDesktopContactForm() {
        this.applyProject(
            {
                userName: this.desktopContactForm!.nameValue(),
                userEmail: this.desktopContactForm!.emailValue(),
                userPhone: this.desktopContactForm!.phoneValue(),
                message: '',
                section: this.desktopContactForm!.getSection() || Section.SECTION_ADFORM_APPLY,
            }
        );
    }

    private applyProject(
        {
            userName,
            userEmail,
            userPhone,
            message,
            section,
        }: {
            userName: string,
            userEmail: string,
            userPhone: string,
            message: string,
            section: Section,
        }) {
        return this.adFormService.applyProject({
            pageViewId: this.pageData.pageViewId,
            projectId: this.pageData.id,
            userName: userName,
            userEmail: userEmail,
            userPhone: userPhone,
            message: message,
            section: section,
        })
            .then(() => {
                this.isSubmitted = true;
            });
    }
}
